import { contaTemBoleto, contaTemNotaFiscal } from '@/utils/contasAnexos';

/**
 * Espelho das regras de documentação contábil (backend/services/contabilidade/documentRules.js).
 */

export type DocTipo = 'comprovante' | 'boleto' | 'nota_fiscal';

export interface ContaDocFields {
  categoria?: string | null;
  forma_pagamento?: string | null;
  comprovante_url?: string | null;
  boleto_url?: string | null;
  nota_fiscal_url?: string | null;
  boletos?: { id: string; url: string; nome_arquivo: string; created_at?: string }[] | null;
  notas_fiscais?: { id: string; url: string; nome_arquivo: string; created_at?: string }[] | null;
  doc_excecao_boleto?: boolean | null;
  doc_excecao_nota_fiscal?: boolean | null;
}

function norm(s: string | null | undefined) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function getRequiredDocs(conta: ContaDocFields): Record<DocTipo, boolean> & { regra: string } {
  const categoria = String(conta.categoria || '');
  const forma = norm(conta.forma_pagamento);
  const boleto = forma.includes('boleto');

  if (norm(categoria).includes('emprestimo') || norm(categoria).includes('financiamento')) {
    return { comprovante: false, boleto: false, nota_fiscal: false, regra: 'emprestimo_financiamento' };
  }
  if (
    categoria === 'Distribuição de Lucros' ||
    categoria === 'Distribuição de Lucro' ||
    categoria === 'Impostos'
  ) {
    return { comprovante: true, boleto: false, nota_fiscal: false, regra: 'distribuicao_ou_imposto' };
  }
  if (categoria === 'Despesa Recorrente') {
    return { comprovante: true, boleto: true, nota_fiscal: false, regra: 'despesa_recorrente' };
  }
  if (categoria === 'Materiais') {
    return boleto
      ? { comprovante: true, boleto: true, nota_fiscal: true, regra: 'materiais_boleto' }
      : { comprovante: true, boleto: false, nota_fiscal: true, regra: 'materiais_pix' };
  }
  return boleto
    ? { comprovante: true, boleto: true, nota_fiscal: true, regra: 'demais_boleto' }
    : { comprovante: true, boleto: false, nota_fiscal: true, regra: 'demais_pix' };
}

export function evaluateContaDocumentacao(conta: ContaDocFields) {
  const required = getRequiredDocs(conta);
  const faltantes: DocTipo[] = [];
  const tipos: DocTipo[] = ['comprovante', 'boleto', 'nota_fiscal'];

  for (const tipo of tipos) {
    if (!required[tipo]) continue;
    if (tipo === 'boleto' && conta.doc_excecao_boleto) continue;
    if (tipo === 'nota_fiscal' && conta.doc_excecao_nota_fiscal) continue;

    let has: boolean;
    switch (tipo) {
      case 'comprovante':
        has = Boolean(conta.comprovante_url);
        break;
      case 'boleto':
        has = contaTemBoleto(conta);
        break;
      case 'nota_fiscal':
        has = contaTemNotaFiscal(conta);
        break;
      default: {
        const _exhaustive: never = tipo;
        throw new Error(`Tipo de documento não tratado: ${_exhaustive}`);
      }
    }
    if (!has) faltantes.push(tipo);
  }

  return {
    required,
    faltantes,
    completa: faltantes.length === 0,
    labelFaltantes: faltantes
      .map((t) => (t === 'nota_fiscal' ? 'NF' : t))
      .join(', '),
  };
}
